import type { Response } from '@app/src/types/resourceExplorer'
import type { ScheduleScreeningFormData } from '../pages/ResourceCollection/Collections/Provider/ScheduleScreening/ScheduleProviderScreeningModal'
import type { Provider } from '../types/organizations'
import { validURL } from './helpers'
import { resolveProviderWebsiteAddress } from './providerEnrichmentUtils'

const getQuestionnaireIdsPerProvider = (alreadyExistingReportedData: Response[]): Map<number, Set<number>> => {
  if (!alreadyExistingReportedData?.length) return new Map()

  const filtered = alreadyExistingReportedData.filter(
    ({ request }) => request.subscriptions?.[0]?.targetProviderId != null && request.questionnaireTemplateId != null,
  )

  const grouped = Object.groupBy(filtered, ({ request }) => request.subscriptions?.[0]?.targetProviderId ?? 0)

  return new Map(
    Object.entries(grouped)
      .filter(([providerIdStr]) => providerIdStr !== '0')
      .map(([providerIdStr, items]) => [
        Number(providerIdStr),
        new Set(
          items
            ?.map(({ request }) => request.questionnaireTemplateId)
            .filter((id): id is number => typeof id === 'number'),
        ),
      ]),
  )
}

const getAlreadyScreenedCount = (
  watchTemplates: ScheduleScreeningFormData['templates'],
  watchProviders: number[],
  reportedMap: Map<number, Set<number>>,
): number => {
  if (!watchTemplates?.length || !watchProviders?.length) return 0

  return watchProviders.reduce((count, providerId) => {
    const reportedTemplates = reportedMap.get(providerId)
    if (!reportedTemplates) return count
    return count + watchTemplates.filter(template => reportedTemplates.has(template.id)).length
  }, 0)
}

const getSelectedProviderIds = (
  providersToConnect: Provider[],
  watchTemplates: ScheduleScreeningFormData['templates'],
  reportedMap: Map<number, Set<number>>,
): number[] => {
  if (!providersToConnect?.length) return []

  return providersToConnect
    .filter(provider => validURL(resolveProviderWebsiteAddress(provider)))
    .filter(provider => {
      const reportedTemplates = reportedMap.get(provider.id)
      if (!watchTemplates?.length || !reportedTemplates) return true
      return watchTemplates.some(t => !reportedTemplates.has(t.id))
    })
    .map(provider => provider.id)
}

const getScreeningInfo = (
  alreadyExistingReportedData: Response[],
  providersToConnect: Provider[],
  watchTemplates: ScheduleScreeningFormData['templates'],
  watchProviders: number[],
): { alreadyScreenedCount: number; validSelectedProviderIds: number[] } => {
  const reportedMap = getQuestionnaireIdsPerProvider(alreadyExistingReportedData)
  return {
    alreadyScreenedCount: getAlreadyScreenedCount(watchTemplates, watchProviders, reportedMap),
    validSelectedProviderIds: getSelectedProviderIds(providersToConnect, watchTemplates, reportedMap),
  }
}

export { getAlreadyScreenedCount, getQuestionnaireIdsPerProvider, getScreeningInfo, getSelectedProviderIds }
